/**
 * Animated videos on the web: the page's path, whether the type is open to
 * this visitor (the server decides; ANIMATED_VIDEO_ENABLED), and the small
 * pure helpers the page uses.
 */
import {
  animatedVideoCreditCost,
  ANIMATED_VIDEO_CREDITS_PER_SECOND,
  ANIMATED_VIDEO_MAX_SECONDS,
  ANIMATED_VIDEO_MIN_SECONDS,
} from "@shared/animatedVideo";
import { trpc } from "@/lib/trpc";

export const ANIMATED_VIDEO_PATH = "/animated-music-video-maker";

/** The server's answer, with the shared constants until it arrives. */
export function useAnimatedVideoConfig() {
  const query = trpc.aiVideo.animatedConfig.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return {
    /** False until the server says otherwise, so nothing flashes for visitors it is closed to. */
    available: query.data?.available === true,
    loaded: query.isFetched,
    creditsPerSecond: query.data?.creditsPerSecond ?? ANIMATED_VIDEO_CREDITS_PER_SECOND,
    maxSeconds: query.data?.maxSeconds ?? ANIMATED_VIDEO_MAX_SECONDS,
    minSeconds: query.data?.minSeconds ?? ANIMATED_VIDEO_MIN_SECONDS,
  };
}

/** Whether the navigation should list the animated video page for this visitor. */
export function useAnimatedVideoAvailable(): boolean {
  return useAnimatedVideoConfig().available;
}

export type AnimatedVideoPlan = {
  /** Seconds of video that will be made (and charged). Null until the song's length is known. */
  seconds: number | null;
  credits: number | null;
  /** The song is longer than the limit: only its first part is used. */
  trimmed: boolean;
  tooShort: boolean;
};

/**
 * What a song of this length turns into: the same arithmetic as the server's
 * create mutation (whole seconds, capped at the limit, 3 credits each).
 */
export function planAnimatedVideo(
  songSeconds: number | null,
  limits: { maxSeconds: number; minSeconds: number },
): AnimatedVideoPlan {
  if (!songSeconds || !Number.isFinite(songSeconds) || songSeconds <= 0) {
    return { seconds: null, credits: null, trimmed: false, tooShort: false };
  }
  const trimmed = songSeconds > limits.maxSeconds;
  const seconds = Math.max(1, Math.floor(Math.min(songSeconds, limits.maxSeconds)));
  return {
    seconds,
    credits: animatedVideoCreditCost(seconds),
    trimmed,
    tooShort: seconds < limits.minSeconds,
  };
}

/**
 * A title for the title card out of a file name: no extension, and no
 * underscores or hyphens standing in for spaces ("my-song_final.mp3"). A name
 * that already has spaces keeps its hyphens ("Semi-Charmed Life.mp3").
 */
export function titleFromFileName(name: string): string {
  const base = name.replace(/\.[^.]+$/, "").trim();
  const spaced = /\s/.test(base) ? base.replace(/_+/g, " ") : base.replace(/[_-]+/g, " ");
  return spaced.replace(/\s+/g, " ").trim();
}
